import { Navigate } from "react-router";
import { useAuthStore } from "@/store/authStore";
import type { ReactNode } from "react";
import type { Permission, UserRole } from "@/types";

interface ProtectedRouteProps {
  children: ReactNode;
  /** One permission, or any-of list (OR). */
  requirePermission?: Permission | readonly Permission[];
  /**
   * Roles redirected home even when they hold the permission (pages that only
   * make sense for another role, e.g. admins have no wallet). Uses the effective
   * role, so impersonation still reaches the page.
   */
  excludeRoles?: readonly UserRole[];
}

/**
 * When used without `requirePermission`, only requires authentication.
 */
export default function ProtectedRoute({
  children,
  requirePermission,
  excludeRoles,
}: ProtectedRouteProps) {
  const { isAuthenticated, hasPermission, user } = useAuthStore();

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  if (requirePermission !== undefined) {
    const required = Array.isArray(requirePermission)
      ? requirePermission
      : [requirePermission];
    if (!required.some((permission) => hasPermission(permission))) {
      return (
        <Navigate
          to="/"
          replace
        />
      );
    }
  }

  if (user && excludeRoles?.includes(user.role)) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  return <>{children}</>;
}
